import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { api } from '@/services/api';
import { usePageTitle } from '@/shared/usePageTitle';
import { useToast } from '@/shared/toast';
import { useConfirm } from '@/shared/confirm';
import { LICENSE_TYPES, LICENSE_LABEL, VVF_GRADES, VVF_GRADE_LABEL, isVvfLicense,
  AUTISTA_VVF_LEVELS, WARNING_DAYS_PERSONAL, getExpiryStatus,
  type LicenseTypeCode, type VvfGrade } from '@vvf/shared';
import { PageHeader } from '@/shared/PageHeader';
import { ExpiryStatusBadge } from '@/shared/ExpiryStatusBadge';

type Tab = 'details' | 'medical' | 'licenses' | 'grade';

export function LockerPage(): React.JSX.Element {
  usePageTitle('Armadietto Personale');
  const [tab, setTab] = useState<Tab>('details');

  return (
    <div className="page-container">
      <PageHeader title="👤 Armadietto Personale" />

      <div className="toolbar">
        {([
          ['details', '📋 Dettagli'],
          ['medical', '🏥 Visite Mediche'],
          ['licenses', '🪪 Patenti'],
          ['grade', '🎖 Gradi'],
        ] as [Tab, string][]).map(([t, label]) => (
          <button key={t} onClick={() => setTab(t)}
            className={tab === t ? 'btn-success' : 'btn-secondary'}>
            {label}
          </button>
        ))}
      </div>

      {tab === 'details' && <DetailsTab />}
      {tab === 'medical' && <MedicalTab />}
      {tab === 'licenses' && <LicensesTab />}
      {tab === 'grade' && <GradeTab />}
    </div>
  );
}

function DetailsTab(): React.JSX.Element {
  const qc = useQueryClient();
  const toast = useToast();
  const { data } = useQuery({
    queryKey: ['locker.details'],
    queryFn: async () => (await api.get('/profile/details')).data,
  });
  const [form, setForm] = useState<Record<string, unknown>>({});
  const current = { ...(data ?? {}), ...form };

  const save = useMutation({
    mutationFn: async () => { await api.put('/profile/details', current); },
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['locker.details'] }); toast.show('Dettagli salvati'); },
  });

  return (
    <div className="card">
      <h3>Dati anagrafici</h3>
      <div className="form-row">
        <div className="form-group">
          <label>Telefono</label>
          <input value={(current.phone as string) ?? ''} onChange={e => setForm({ ...form, phone: e.target.value })} />
        </div>
        <div className="form-group">
          <label>Email contatto</label>
          <input type="email" value={(current.contactEmail as string) ?? ''}
            onChange={e => setForm({ ...form, contactEmail: e.target.value })} />
        </div>
      </div>
      <div className="form-row">
        <div className="form-group">
          <label>Data di nascita</label>
          <input type="date" value={(current.birthDate as string) ?? ''}
            onChange={e => setForm({ ...form, birthDate: e.target.value })} />
        </div>
        <div className="form-group">
          <label>Luogo di nascita</label>
          <input value={(current.birthPlace as string) ?? ''}
            onChange={e => setForm({ ...form, birthPlace: e.target.value })} />
        </div>
      </div>
      <div className="form-row">
        <div className="form-group">
          <label>Codice Fiscale</label>
          <input maxLength={16} value={(current.fiscalCode as string) ?? ''}
            onChange={e => setForm({ ...form, fiscalCode: e.target.value.toUpperCase() })} />
        </div>
        <div className="form-group">
          <label>Indirizzo</label>
          <input value={(current.address as string) ?? ''}
            onChange={e => setForm({ ...form, address: e.target.value })} />
        </div>
      </div>
      <button onClick={() => save.mutate()} disabled={save.isPending} className="btn-success">
        💾 Salva dettagli
      </button>
    </div>
  );
}

function MedicalTab(): React.JSX.Element {
  const qc = useQueryClient();
  const toast = useToast();
  const confirm = useConfirm();
  const { data } = useQuery({
    queryKey: ['locker.medical'],
    queryFn: async () => (await api.get('/profile/medical-checks')).data,
  });
  const [form, setForm] = useState({ lastCheckDate: '', expiryDate: '', sixMonthsBeforeExpiry: '', notes: '' });
  const add = useMutation({
    mutationFn: async () => {
      // ASP.NET non parsa "" come DateOnly?: serve null per campi opzionali vuoti
      const payload = {
        lastCheckDate: form.lastCheckDate || null,
        expiryDate: form.expiryDate || null,
        sixMonthsBeforeExpiry: form.sixMonthsBeforeExpiry || null,
        notes: form.notes || null,
      };
      await api.post('/profile/medical-checks', payload);
    },
    onSuccess: () => {
      setForm({ lastCheckDate: '', expiryDate: '', sixMonthsBeforeExpiry: '', notes: '' });
      qc.invalidateQueries({ queryKey: ['locker.medical'] });
      toast.show('Visita aggiunta');
    },
    onError: (e: unknown) => {
      const msg = (e as { response?: { data?: { error?: string } } })?.response?.data?.error
        ?? 'Errore salvataggio visita';
      toast.show(msg, 'error');
    },
  });
  const del = useMutation({
    mutationFn: async (id: string) => { await api.delete(`/profile/medical-checks/${id}`); },
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['locker.medical'] }); toast.show('Eliminata'); },
  });

  return (
    <>
      <div className="card">
        <h3>Aggiungi visita medica</h3>
        <div className="form-row">
          <div className="form-group">
            <label>Data visita</label>
            <input type="date" value={form.lastCheckDate}
              onChange={e => setForm({ ...form, lastCheckDate: e.target.value })} />
          </div>
          <div className="form-group">
            <label>Scadenza</label>
            <input type="date" value={form.expiryDate}
              onChange={e => setForm({ ...form, expiryDate: e.target.value })} />
          </div>
        </div>
        <div className="form-row">
          <div className="form-group">
            <label>Scadenza −6 mesi (soglia avviso)</label>
            <input type="date" value={form.sixMonthsBeforeExpiry}
              onChange={e => setForm({ ...form, sixMonthsBeforeExpiry: e.target.value })} />
          </div>
        </div>
        {/* F14-allineamento: salvataggio PARZIALE — basta UN campo (data visita o scadenza). */}
        <button onClick={() => add.mutate()} disabled={add.isPending || (!form.lastCheckDate && !form.expiryDate)} className="btn-success">
          + Aggiungi
        </button>
      </div>

      <div className="table-wrapper">
        <table>
          <thead><tr><th>Data</th><th>Scadenza</th><th>Stato</th><th></th></tr></thead>
          <tbody>
            {(data ?? []).map((m: { id: string; lastCheckDate: string | null; expiryDate: string | null }) => {
              const status = getExpiryStatus(m.expiryDate, WARNING_DAYS_PERSONAL);
              return (
                <tr key={m.id}>
                  <td>{m.lastCheckDate ?? '-'}</td>
                  <td>{m.expiryDate ?? '-'}</td>
                  <td><ExpiryStatusBadge status={status} /></td>
                  <td><button className="btn-delete" onClick={async () => {
                    if (await confirm({ message: 'Eliminare questa visita?', variant: 'danger' })) del.mutate(m.id);
                  }}>🗑</button></td>
                </tr>
              );
            })}
            {(data ?? []).length === 0 && <tr><td colSpan={4}><div className="empty-state">Nessuna visita</div></td></tr>}
          </tbody>
        </table>
      </div>
    </>
  );
}

function LicensesTab(): React.JSX.Element {
  const qc = useQueryClient();
  const toast = useToast();
  const confirm = useConfirm();
  const { data } = useQuery({
    queryKey: ['locker.licenses'],
    queryFn: async () => (await api.get('/profile/licenses')).data,
  });
  const initialForm = {
    type: 'B' as LicenseTypeCode, number: '', issuedAt: '', expiryDate: '',
    sixMonthsBeforeExpiry: '', vvfLicenseGrade: '', notes: '',
  };
  const [form, setForm] = useState(initialForm);
  const add = useMutation({
    mutationFn: async () => {
      // Stesso problema delle visite: campi opzionali vuoti devono essere null,
      // non "", altrimenti ASP.NET fallisce a parsare DateOnly?
      const payload = {
        type: form.type,
        number: form.number || null,
        issuedAt: form.issuedAt || null,
        expiryDate: form.expiryDate || null,
        sixMonthsBeforeExpiry: form.sixMonthsBeforeExpiry || null,
        vvfLicenseGrade: form.vvfLicenseGrade || null,
        notes: form.notes || null,
      };
      await api.post('/profile/licenses', payload);
    },
    onSuccess: () => {
      setForm(initialForm);
      qc.invalidateQueries({ queryKey: ['locker.licenses'] });
      toast.show('Patente aggiunta');
    },
    onError: (e: unknown) => {
      const msg = (e as { response?: { data?: { error?: string } } })?.response?.data?.error
        ?? 'Errore salvataggio patente';
      toast.show(msg, 'error');
    },
  });
  const del = useMutation({
    mutationFn: async (id: string) => { await api.delete(`/profile/licenses/${id}`); },
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['locker.licenses'] }); toast.show('Eliminata'); },
  });

  return (
    <>
      <div className="card">
        <h3>Aggiungi patente</h3>
        <div className="form-row">
          <div className="form-group">
            <label>Tipo</label>
            <select value={form.type} onChange={e => setForm({ ...form, type: e.target.value as LicenseTypeCode })}>
              {LICENSE_TYPES.map(t => <option key={t} value={t}>{LICENSE_LABEL[t]}</option>)}
            </select>
          </div>
          <div className="form-group">
            <label>Numero</label>
            <input value={form.number} onChange={e => setForm({ ...form, number: e.target.value })} />
          </div>
        </div>
        <div className="form-row">
          <div className="form-group">
            <label>Rilascio</label>
            <input type="date" value={form.issuedAt} onChange={e => setForm({ ...form, issuedAt: e.target.value })} />
          </div>
          <div className="form-group">
            <label>Scadenza</label>
            <input type="date" value={form.expiryDate} onChange={e => setForm({ ...form, expiryDate: e.target.value })} />
          </div>
        </div>
        <div className="form-row">
          <div className="form-group">
            <label>Scadenza −6 mesi (soglia avviso)</label>
            <input type="date" value={form.sixMonthsBeforeExpiry}
              onChange={e => setForm({ ...form, sixMonthsBeforeExpiry: e.target.value })} />
          </div>
        </div>
        {isVvfLicense(form.type) && (
          <div className="form-group">
            <label>Grado patente VVF</label>
            <input value={form.vvfLicenseGrade} placeholder="Es. VVF I grado"
              onChange={e => setForm({ ...form, vvfLicenseGrade: e.target.value })} />
          </div>
        )}
        {/* F14-allineamento: il Tipo (sempre selezionato) e' l'unico campo richiesto;
            scadenza/rilascio/numero sono opzionali (salvataggio parziale). */}
        <button onClick={() => add.mutate()} disabled={add.isPending} className="btn-success">
          + Aggiungi patente
        </button>
      </div>

      <div className="table-wrapper">
        <table>
          <thead><tr><th>Tipo</th><th>Numero</th><th>Rilascio</th><th>Scadenza</th><th>Stato</th><th></th></tr></thead>
          <tbody>
            {(data ?? []).map((l: { id: string; type: LicenseTypeCode; number?: string; issuedAt?: string; expiryDate: string | null }) => {
              const status = getExpiryStatus(l.expiryDate, WARNING_DAYS_PERSONAL);
              return (
                <tr key={l.id}>
                  <td>{LICENSE_LABEL[l.type] ?? l.type}</td>
                  <td>{l.number ?? '-'}</td>
                  <td>{l.issuedAt ?? '-'}</td>
                  <td>{l.expiryDate ?? '-'}</td>
                  <td><ExpiryStatusBadge status={status} /></td>
                  <td><button className="btn-delete" onClick={async () => {
                    if (await confirm({ message: 'Eliminare questa patente?', variant: 'danger' })) del.mutate(l.id);
                  }}>🗑</button></td>
                </tr>
              );
            })}
            {(data ?? []).length === 0 && <tr><td colSpan={6}><div className="empty-state">Nessuna patente</div></td></tr>}
          </tbody>
        </table>
      </div>
    </>
  );
}

function GradeTab(): React.JSX.Element {
  const qc = useQueryClient();
  const toast = useToast();
  const { data } = useQuery({
    queryKey: ['locker.details'],
    queryFn: async () => (await api.get('/profile/details')).data,
  });
  const [form, setForm] = useState<Record<string, unknown>>({});
  const current = { ...(data ?? {}), ...form };
  const save = useMutation({
    mutationFn: async () => { await api.put('/profile/details', current); },
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['locker.details'] }); toast.show('Gradi e competenze salvati'); },
  });

  const competenze: Array<[string, string]> = [
    ['competenceAutista', 'Autista'],
    ['competenceTpss', 'TPSS'],
    ['competenceAtp', 'ATP'],
    ['competenceSaf', 'SAF'],
    ['competenceNbcr', 'NBCR'],
    ['competenceTas', 'TAS'],
  ];

  const isAutista = Boolean(current.competenceAutista);
  const autistaLevel = (current.competenceAutistaVvfLevel as string | undefined) ?? '';
  const gradeLabel = current.vvfGrade ? VVF_GRADE_LABEL[current.vvfGrade as VvfGrade] : null;
  const selectedCompetenze = competenze
    .filter(([key]) => Boolean(current[key]))
    .map(([key, label]) => key === 'competenceAutista' && autistaLevel ? `${label} (${autistaLevel})` : label);

  return (
    <div className="card">
      <h3>Grado VVF e competenze</h3>
      <div className="form-group">
        <label>Grado VVF</label>
        <select value={(current.vvfGrade as string) ?? ''} onChange={e => setForm({ ...form, vvfGrade: e.target.value as VvfGrade })}>
          <option value="">—</option>
          {VVF_GRADES.map(g => <option key={g} value={g}>{VVF_GRADE_LABEL[g]}</option>)}
        </select>
      </div>
      <div className="form-row">
        {competenze.map(([key, label]) => (
          <label key={key} className="checkbox-group">
            <input type="checkbox" checked={Boolean(current[key])}
              onChange={e => setForm({ ...form, [key]: e.target.checked })} />
            {label}
          </label>
        ))}
      </div>

      {/* Dropdown patente VVF condizionale (richiesta Bryan: appare quando Autista e' selezionato) */}
      {isAutista && (
        <div className="form-group locker-autista-box">
          <label>Livello abilitazione patente Autista</label>
          <select value={autistaLevel}
            onChange={e => setForm({ ...form, competenceAutistaVvfLevel: e.target.value })}>
            <option value="">— seleziona livello —</option>
            {AUTISTA_VVF_LEVELS.map(lv => <option key={lv} value={lv}>{lv}</option>)}
          </select>
        </div>
      )}

      <button onClick={() => save.mutate()} disabled={save.isPending} className="btn-success">💾 Salva</button>

      {/* Riepilogo selezione (richiesta Bryan: visualizzazione attuali grado + competenze) */}
      <div className="locker-grade-summary">
        <strong className="locker-grade-summary-title">📋 Riepilogo:</strong>
        <div className="locker-grade-summary-row">
          <strong>Grado VVF:</strong> {gradeLabel ?? <em className="locker-grade-empty">non specificato</em>}
        </div>
        <div className="locker-grade-summary-row">
          <strong>Competenze:</strong>{' '}
          {selectedCompetenze.length > 0
            ? selectedCompetenze.join(', ')
            : <em className="locker-grade-empty">nessuna competenza selezionata</em>}
        </div>
      </div>
    </div>
  );
}
